import type {
  BrowserCommand,
  BrowserDisplaySession,
  BrowserSessionSnapshot,
  BrowserTabSnapshot,
} from "@cmmd-forge/contracts";
import { browserCommandUsesInteractiveInput } from "./BrowserPreviewPanel.commands";
import { isFreshBrowserCapture } from "./BrowserPreviewPanel.capture";
import {
  normalizeBrowserDisplayForPreview,
  shouldAttachRemoteWebRtcDisplay,
} from "./BrowserPreviewPanel.remoteWebRtcDisplay";
import { isBlankBrowserUrl, resolveBrowserRenderMode } from "./BrowserPreviewPanel.url";

export type BrowserPreviewStatus =
  | "no-session"
  | "connecting"
  | "idle"
  | "runtime-unavailable"
  | "failed"
  | "navigation-pending"
  | "navigation-failed"
  | "capture-pending"
  | "running";

export interface BrowserPreviewVm {
  readonly status: BrowserPreviewStatus;
  readonly title: string;
  readonly description: string;
  readonly canRenderCanvas: boolean;
  readonly canCaptureScreenshot: boolean;
  readonly canAnnotate: boolean;
}

export function browserErrorLooksLikeRuntimeTimeout(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : String(error);
  return /browser runtime in time|timed out|timeout/i.test(message);
}

export function remoteWebRtcFailureReasonForMessage(
  message: string,
): BrowserDisplaySession["reason"] {
  if (/requires a TURN relay|TURN relay unavailable|media could not route/i.test(message)) {
    return "network_unreachable";
  }
  if (/RTCPeerConnection/i.test(message)) {
    return "unsupported_host";
  }
  return "unknown";
}

export function resolveRemoteWebRtcInputTimeoutRecoveryDisplay(input: {
  readonly command: BrowserCommand;
  readonly error: unknown;
  readonly useNativeBrowserCommand: boolean;
  readonly display: BrowserDisplaySession | null | undefined;
}): BrowserDisplaySession | null {
  if (
    input.useNativeBrowserCommand ||
    !browserCommandUsesInteractiveInput(input.command) ||
    input.display?.transport !== "remote-webrtc" ||
    !shouldAttachRemoteWebRtcDisplay(input.display) ||
    !browserErrorLooksLikeRuntimeTimeout(input.error)
  ) {
    return null;
  }

  return {
    transport: "remote-webrtc",
    status: "reconnecting",
    reason: "transport_unavailable",
    detail: "Reconnecting remote WebRTC browser display after an input timeout.",
    ...(input.display.viewport ? { viewport: input.display.viewport } : {}),
    ...(input.display.devicePixelRatio ? { devicePixelRatio: input.display.devicePixelRatio } : {}),
    ...(input.display.iceServers ? { iceServers: input.display.iceServers } : {}),
    reconnect: { status: "ready", attempt: (input.display.reconnect?.attempt ?? 0) + 1 },
  };
}

function browserDisplayUnavailableMessage(display: BrowserSessionSnapshot["display"]): {
  readonly title: string;
  readonly description: string;
} {
  switch (display.status) {
    case "degraded":
      return {
        title: "Browser display degraded",
        description: display.detail ?? "The live browser display is currently degraded.",
      };
    case "failed":
      return {
        title: "Browser display failed",
        description: display.detail ?? "The live browser display failed.",
      };
    case "unsupported":
      return {
        title: "Browser display unsupported",
        description: display.detail ?? "This environment does not support a live browser display.",
      };
    case "connecting":
    case "reconnecting":
      return {
        title:
          display.status === "reconnecting"
            ? "Reconnecting browser display"
            : "Connecting live preview",
        description:
          display.detail ?? "Forge is attaching the real browser display transport for this tab.",
      };
    case "connected":
      return {
        title: "Connecting live preview",
        description: "Forge is starting the interactive browser stream for this tab.",
      };
  }
}

export function resolveBrowserPreviewVm(input: {
  readonly hasBrowserRuntime: boolean;
  readonly runtimeError: string | null;
  readonly session: BrowserSessionSnapshot | null;
  readonly activeTab: BrowserTabSnapshot | null;
  readonly activeScreenshotDataUrl: string | undefined;
  readonly activeScreenshotUrl: string | undefined;
  readonly activeScreenshotCapturedAt?: string | undefined;
  readonly activeScreenshotViewport?:
    | { readonly width: number; readonly height: number }
    | null
    | undefined;
  readonly activeFrameDataUrl?: string | undefined;
  readonly activeFrameUrl?: string | undefined;
  readonly activeFrameCapturedAt?: string | undefined;
  readonly activeFrameViewport?:
    | { readonly width: number; readonly height: number }
    | null
    | undefined;
  readonly connecting?: boolean;
}): BrowserPreviewVm {
  if (!input.hasBrowserRuntime) {
    return {
      status: "runtime-unavailable",
      title: "Browser runtime unavailable",
      description: input.runtimeError ?? "This environment cannot start an in-app browser session.",
      canRenderCanvas: false,
      canCaptureScreenshot: false,
      canAnnotate: false,
    };
  }

  if (input.runtimeError && !input.session) {
    return {
      status: "failed",
      title: "Browser action failed",
      description: input.runtimeError,
      canRenderCanvas: false,
      canCaptureScreenshot: false,
      canAnnotate: false,
    };
  }

  if (input.connecting === true && !input.session) {
    return {
      status: "connecting",
      title: "Connecting browser session…",
      description: "Forge is preparing a sandboxed browser for this thread.",
      canRenderCanvas: false,
      canCaptureScreenshot: false,
      canAnnotate: false,
    };
  }

  if (!input.session) {
    return {
      status: "no-session",
      title: "Start a browser preview",
      description: "Enter a URL to open a browser tab for this thread.",
      canRenderCanvas: false,
      canCaptureScreenshot: false,
      canAnnotate: false,
    };
  }

  // Session-ready vs page-ready: the pre-navigation snapshot parks the tab on
  // about:blank while navigation settles. Pending, failed, and intentionally
  // blank tabs are three distinct visible states: never an error flash for
  // pending, never a silent blank for failed.
  if (
    input.activeTab &&
    isBlankBrowserUrl(input.activeTab.url) &&
    input.activeTab.navigationStatus === "pending"
  ) {
    return {
      status: "navigation-pending",
      title: "Opening page…",
      description: input.activeTab.requestedUrl
        ? `Loading ${input.activeTab.requestedUrl}…`
        : "Forge is opening the requested page.",
      canRenderCanvas: false,
      canCaptureScreenshot: false,
      canAnnotate: false,
    };
  }

  if (
    input.activeTab &&
    isBlankBrowserUrl(input.activeTab.url) &&
    input.activeTab.navigationStatus === "failed"
  ) {
    return {
      status: "navigation-failed",
      title: "Page failed to load",
      description: `${input.activeTab.requestedUrl ?? "The requested page"} could not be opened. ${
        input.activeTab.navigationError ?? "Navigation failed before the page could load."
      } Enter the address to retry.`,
      canRenderCanvas: false,
      canCaptureScreenshot: false,
      canAnnotate: false,
    };
  }

  if (!input.activeTab || isBlankBrowserUrl(input.activeTab.url)) {
    return {
      status: "idle",
      title: "No page open",
      description:
        "Enter a URL to load a page. Blank tabs are kept internal to the browser runtime.",
      canRenderCanvas: false,
      canCaptureScreenshot: false,
      canAnnotate: false,
    };
  }

  if (input.activeTab.status === "degraded" || input.activeTab.lastError) {
    return {
      status: "failed",
      title: "Preview failed",
      description:
        input.activeTab.lastError ??
        "Forge could not restore this tab. Reload it or open the page again.",
      canRenderCanvas: false,
      canCaptureScreenshot: false,
      canAnnotate: false,
    };
  }

  const renderMode = input.activeTab.renderMode ?? resolveBrowserRenderMode(input.activeTab.url);
  if (renderMode === "interactive") {
    const display = normalizeBrowserDisplayForPreview(input.session.display);
    if (display.status !== "connected") {
      const message = browserDisplayUnavailableMessage(display);
      const canAttachRemoteDisplay = shouldAttachRemoteWebRtcDisplay(display);
      return {
        status:
          display.status === "failed" || display.status === "unsupported"
            ? "failed"
            : "capture-pending",
        title: message.title,
        description: message.description,
        canRenderCanvas: canAttachRemoteDisplay,
        canCaptureScreenshot: true,
        canAnnotate: false,
      };
    }
    if (input.runtimeError) {
      return {
        status: "failed",
        title: "Browser action failed",
        description: input.runtimeError,
        canRenderCanvas: false,
        canCaptureScreenshot: false,
        canAnnotate: false,
      };
    }
    return {
      status: "running",
      title: input.activeTab.title ?? input.activeTab.url,
      description: input.activeTab.url,
      canRenderCanvas: true,
      canCaptureScreenshot: true,
      canAnnotate: false,
    };
  }

  if (input.runtimeError) {
    return {
      status: "failed",
      title: "Browser action failed",
      description: input.runtimeError,
      canRenderCanvas: false,
      canCaptureScreenshot: false,
      canAnnotate: false,
    };
  }

  if (
    !input.activeScreenshotDataUrl ||
    !isFreshBrowserCapture({
      tabUrl: input.activeTab.url,
      captureUrl: input.activeScreenshotUrl,
      capturedAt: input.activeScreenshotCapturedAt,
      requestedAt: input.activeTab.updatedAt,
      sessionViewport: input.session.viewport,
      captureViewport: input.activeScreenshotViewport,
    })
  ) {
    return {
      status: "capture-pending",
      title: "Loading browser capture",
      description: "Forge opened the page and is waiting for a fresh visible capture.",
      canRenderCanvas: false,
      canCaptureScreenshot: true,
      canAnnotate: false,
    };
  }

  return {
    status: "running",
    title: input.activeTab.title ?? input.activeTab.url,
    description: input.activeTab.url,
    canRenderCanvas: true,
    canCaptureScreenshot: true,
    canAnnotate: true,
  };
}
